import { useState } from 'react';
import { Link } from 'react-router';

import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { useQuickAddOptions } from '../../transactions/hooks/useQuickAdd.js';
import { TemplateForm } from '../components/TemplateForm.js';
import {
  restoreRecurring,
  setTemplateActive,
  type RecurringTemplate,
} from '../data/recurringRepository.js';
import { useTemplates } from '../hooks/useTemplates.js';
import { AMOUNT_MODE_LABELS, FREQUENCY_LABELS, dueDateLabel } from '../model/recurringLabels.js';
import styles from './RecurringPage.module.css';

/**
 * "Khoản định kỳ" — the bills the household knows are coming (plan step 1.5, spec §11).
 *
 * A template is a forecast, never a posted entry: creating one writes nothing to the ledger.
 * What it does is put "Đến hạn: Internet 300k [Xác nhận]" on the home screen, so the monthly
 * round of fixed payments is one tap each instead of a full form each (B1).
 *
 * This page is the index only: one row per template, with Sửa (changes the template from the
 * next unconfirmed occurrence onwards, R3) and Tắt. Everything about a template's individual
 * occurrences — what was posted, skipped, undone, and the way back from each — lives on
 * /dinh-ky/:templateId (decision AJ3): after years of use the per-month rows would otherwise
 * make this list as long as the household's history.
 */
export function RecurringPage() {
  const contextState = useCurrentContext();
  const templatesState = useTemplates();
  const optionsState = useQuickAddOptions();

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RecurringTemplate | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (contextState.status === 'loading' || templatesState.status === 'loading') {
    return <LoadingState label="Đang tải khoản định kỳ…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (templatesState.status === 'error') {
    return <ErrorState message={templatesState.message} onRetry={templatesState.retry} />;
  }

  const { context } = contextState;
  const { refresh } = templatesState;
  const timeZone = context.household.timezone;
  const active = templatesState.templates.filter((template) => template.isActive);
  const retired = templatesState.templates.filter((template) => !template.isActive);

  async function restore(template: RecurringTemplate) {
    if (template.lastOccurrence === null) return;
    setBusyId(template.id);
    setMessage(null);
    try {
      await restoreRecurring({
        templateId: template.id,
        dueDate: template.lastOccurrence.dueDate,
        reason: 'Khôi phục ở Khoản định kỳ',
      });
      setNotice(
        `Đã khôi phục kỳ ${dueDateLabel(template.lastOccurrence.dueDate, timeZone)} của "${template.name}" — hiện lại ở Đến hạn.`,
      );
      await refresh();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function toggle(template: RecurringTemplate) {
    setBusyId(template.id);
    setMessage(null);
    try {
      await setTemplateActive(template.id, !template.isActive);
      setNotice(
        template.isActive ? `Đã tắt "${template.name}".` : `Đã bật lại "${template.name}".`,
      );
      await refresh();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Khoản định kỳ</h1>
        {!isFormOpen ? (
          <button type="button" className={styles.addButton} onClick={() => setFormOpen(true)}>
            + Thêm khoản định kỳ
          </button>
        ) : null}
      </div>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {(isFormOpen || editing !== null) && optionsState.status === 'ready' ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>
            {editing === null ? 'Khoản định kỳ mới' : `Sửa "${editing.name}"`}
          </h2>
          <TemplateForm
            // Remounting on the template being edited is what makes the prefill reload when
            // the user jumps straight from one template's Sửa to another's.
            key={editing?.id ?? 'new'}
            options={optionsState.options}
            householdId={context.household.id}
            subjectId={context.selfSubject.id}
            timeZone={timeZone}
            {...(editing === null ? {} : { initial: editing })}
            onCancel={() => {
              setFormOpen(false);
              setEditing(null);
            }}
            onCreated={(name) => {
              const wasEdit = editing !== null;
              setFormOpen(false);
              setEditing(null);
              setNotice(
                wasEdit
                  ? `Đã lưu "${name}". Áp dụng từ kỳ chưa ghi kế tiếp.`
                  : `Đã thêm "${name}". Đến hạn sẽ hiện ở Trang chủ.`,
              );
              void refresh();
            }}
          />
        </section>
      ) : null}

      {templatesState.templates.length === 0 && !isFormOpen ? (
        <EmptyState
          icon="🔁"
          title="Chưa có khoản định kỳ nào"
          message="Thêm tiền nhà, điện, internet, bảo hiểm… để mỗi tháng chỉ phải xác nhận một chạm."
        />
      ) : null}

      <TemplateList
        title="Đang dùng"
        templates={active}
        onRestore={(template) => void restore(template)}
        timeZone={timeZone}
        busyId={busyId}
        onToggle={(template) => void toggle(template)}
        onEdit={(template) => {
          setFormOpen(false);
          setEditing(template);
          setMessage(null);
        }}
      />
      <TemplateList
        title="Đã tắt"
        templates={retired}
        timeZone={timeZone}
        busyId={busyId}
        onToggle={(template) => void toggle(template)}
      />
    </div>
  );
}

function TemplateList({
  title,
  templates,
  timeZone,
  busyId,
  onToggle,
  onRestore,
  onEdit,
}: {
  title: string;
  templates: readonly RecurringTemplate[];
  timeZone: string;
  busyId: string | null;
  onToggle: (template: RecurringTemplate) => void;
  /** Takes back the last skip of a template (AJ1); absent for retired ones. */
  onRestore?: (template: RecurringTemplate) => void;
  /** Absent for retired templates: editing something switched off has no next occurrence. */
  onEdit?: (template: RecurringTemplate) => void;
}) {
  if (templates.length === 0) return null;

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>{title}</h2>
      <ul className={lists.list}>
        {templates.map((template) => (
          <li key={template.id}>
            <div className={cx(styles.row, !template.isActive && styles.rowMuted)}>
              <span className={lists.rowText}>
                <Link className={lists.rowName} to={`/dinh-ky/${template.id}`}>
                  {template.name}
                </Link>
                <span className={lists.rowMeta}>
                  {FREQUENCY_LABELS[template.frequency]} · {AMOUNT_MODE_LABELS[template.amountMode]}
                  {template.isActive ? ` · ${dueDateLabel(template.nextDueDate, timeZone)}` : ''}
                </span>
                {template.isActive && template.lastOccurrence?.action === 'skipped' ? (
                  <span className={lists.rowMeta}>
                    Đã bỏ qua kỳ {dueDateLabel(template.lastOccurrence.dueDate, timeZone)}
                    {onRestore === undefined ? null : (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className={styles.textButton}
                          disabled={busyId === template.id}
                          aria-label={`Khôi phục kỳ đã bỏ qua của ${template.name}`}
                          onClick={() => onRestore(template)}
                        >
                          Khôi phục
                        </button>
                      </>
                    )}
                  </span>
                ) : null}
              </span>

              <span className={styles.rowRight}>
                {/* An estimated template has no amount to show — that is the whole point. */}
                {template.amountMode === 'fixed' ? (
                  <span className={lists.amount}>{formatVnd(totalOf(template))}</span>
                ) : null}
                {onEdit === undefined ? null : (
                  <button
                    type="button"
                    className={styles.textButton}
                    disabled={busyId === template.id}
                    aria-label={`Sửa ${template.name}`}
                    onClick={() => onEdit(template)}
                  >
                    Sửa
                  </button>
                )}
                <button
                  type="button"
                  className={styles.textButton}
                  disabled={busyId === template.id}
                  aria-label={`${template.isActive ? 'Tắt' : 'Bật lại'} ${template.name}`}
                  onClick={() => onToggle(template)}
                >
                  {template.isActive ? 'Tắt' : 'Bật lại'}
                </button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What the template takes out of the account: its debit side, as stored. */
function totalOf(template: RecurringTemplate): number {
  return template.lines
    .filter((line) => line.side === 'debit')
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);
}
